/**
 * KNK-1.11 / FR-11 — Settings → Knock Mode.
 *
 * Only while Knock Mode is on (FR-11: when off, no knocking setting appears anywhere). The
 * switch itself moved to Settings → Add-on modules in KNK-1.55 (`AccountModulesSettings`).
 *
 * ⚠️ Nothing here is the gate. The database refuses everything this screen hides: see
 * `lib/knockMode.ts`. Rules and copy live there; this file renders.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { useKnockMode } from '@/hooks/useKnockMode';
import {
  KNOCK_MODE_COPY as COPY,
  buttonsLine,
  confirmedLine,
  knockModeSettingsVisible,
  type KnockAttestedKey,
  type KnockModeStatus,
  type KnockReadinessItem,
} from '@/lib/knockMode';
import { fetchKnockModeStatus, setKnockReadinessItem } from '@/services/knockModeService';
import { KnockActivationCard } from './KnockActivationCard';
import { KnockImportCard } from './KnockImportCard';
import { KnockDispositionSettings } from './KnockDispositionSettings';
import { OfficesCard } from './OfficesCard';
import { FieldProfilesCard } from './FieldProfilesCard';
import { KnockInvitesCard } from './KnockInvitesCard';
import { LocationPolicyReview } from './LocationPolicyReview';

export function KnockModeSettings() {
  const { hasAnyPermission, loading: permsLoading } = usePermissions();
  const { status, loading, error, setStatus } = useKnockMode();
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  // KNK-1.7: placing a team under an Office changes the Office each profile shows.
  const [officeChanges, setOfficeChanges] = useState(0);

  const canManageField = hasAnyPermission(['field.settings.manage', 'system.override']);
  // KNK-1.53: the server asks for both, lead.import (imports and batch undo) and field.settings.manage.
  const canImport = canManageField && hasAnyPermission(['lead.import', 'system.override']);

  if (!knockModeSettingsVisible({ status, loading: loading || permsLoading, canManageField })) {
    return null;
  }

  const fail = (e: unknown) =>
    toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });

  const attest = async (key: KnockAttestedKey, confirmed: boolean) => {
    setBusy(key);
    try {
      setStatus(await setKnockReadinessItem(key, confirmed));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>Could not read Knock Mode: {error}</AlertDescription>
        </Alert>
      )}

      {status.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {COPY.checklistTitle}
              <Badge variant={status.ready ? 'default' : 'secondary'}>{status.ready ? 'Ready' : 'Not ready'}</Badge>
            </CardTitle>
            <CardDescription>{status.ready ? COPY.checklistReady : COPY.checklistNotReady}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="divide-y divide-border rounded-md border border-border">
              {status.items.map((item) => (
                <ChecklistRow
                  key={item.key}
                  item={item}
                  canManage={canManageField}
                  busy={busy}
                  onAttest={attest}
                  onStatus={setStatus}
                  onError={fail}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* KNK-1.22. Same rule; create_knock_invite decides who may invite. */}
      {status.enabled && canManageField && <KnockInvitesCard />}

      {/* KNK-1.52. Same rule as the settings below: on, and field.settings.manage. */}
      {status.enabled && canManageField && <KnockActivationCard />}

      {/* KNK-1.53. */}
      {status.enabled && canImport && <KnockImportCard />}

      {/* KNK-1.7. Same rule: on, and field.settings.manage. */}
      {status.enabled && canManageField && <OfficesCard onChanged={() => setOfficeChanges((n) => n + 1)} />}
      {status.enabled && canManageField && <FieldProfilesCard reloadKey={officeChanges} />}

      {/* KNK-1.12. Only while on, and only for field.settings.manage: the RLS on both tables
          refuses everyone else's writes anyway, but FR-11 says nobody else sees the settings. */}
      {status.enabled && canManageField && <KnockDispositionSettings
          // A quiet re-read: reload() would flip loading, unmount this panel and lose its state.
          onQuickButtonsChanged={() => void fetchKnockModeStatus().then(setStatus).catch(() => undefined)}
        />}
    </div>
  );
}

function ChecklistRow({
  item,
  canManage,
  busy,
  onAttest,
  onStatus,
  onError,
}: {
  item: KnockReadinessItem;
  canManage: boolean;
  busy: string | null;
  onAttest: (key: KnockAttestedKey, confirmed: boolean) => void;
  onStatus: (s: KnockModeStatus) => void;
  onError: (e: unknown) => void;
}) {
  const copy = COPY.items[item.key];
  const line = confirmedLine(item);
  const buttons = buttonsLine(item);
  const attestable = item.key !== 'doors';
  // KNK-1.37: location_policy can be attested and still fail for want of counsel's review, so the
  // button follows the attestation, not the pass.
  const attested = item.key === 'location_policy' ? item.confirmedAt !== null : item.passed;

  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`knock-readiness-${item.key}`}>
      <div className="flex gap-3">
        {item.passed ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-label="Passed" />
        ) : (
          <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-label="Not passed" />
        )}
        <div className="space-y-1">
          <p className="font-medium text-foreground">{copy.label}</p>
          <p className="text-sm text-muted-foreground">{copy.detail}</p>
          {buttons && <p className="text-xs text-muted-foreground">{buttons}</p>}
          {line && <p className="text-xs text-muted-foreground">{line}</p>}
          {/* KNK-1.37: counsel's review, the policy text, and recording a review. */}
          {item.key === 'location_policy' && (
            <LocationPolicyReview item={item} canManage={canManage} onStatus={onStatus} onError={onError} />
          )}
        </div>
      </div>
      {/* KNK-1.14. Counted, not confirmed: the way to pass it is to seed. */}
      {!attestable && (
        <Button asChild size="sm" variant={item.passed ? 'outline' : 'default'} className="shrink-0 self-start">
          <Link to="/app/doors">{item.passed ? 'Open the Door map' : 'Seed Doors'}</Link>
        </Button>
      )}
      {attestable && canManage && (
        <Button
          size="sm"
          variant={attested ? 'outline' : 'default'}
          disabled={busy !== null}
          onClick={() => onAttest(item.key as KnockAttestedKey, !attested)}
          className="shrink-0 self-start"
        >
          {busy === item.key && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {attested ? COPY.withdraw : COPY.confirm}
        </Button>
      )}
    </li>
  );
}
